import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FamilyProvider } from './contexts/FamilyContext';
import { useIsAdmin } from './utils/admin';

// Pages
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import ExperiencePlanning from './pages/ExperiencePlanning';
import Experiences from './pages/Experiences';
import AcceptInvite from './pages/AcceptInvite';
import Explore from './pages/Explore';
import RecipeDetail from './pages/RecipeDetail';
import ActivityDetail from './pages/ActivityDetail';
import StoryDetail from './pages/StoryDetail';
import Community from './pages/Community';
import Settings from './pages/Settings';
import SubmitContent from './pages/SubmitContent';
import Chats from './pages/Chats';
import ChatDetail from './pages/ChatDetail';
import Admin from './pages/Admin';
import AdminDashboard from './pages/admin/AdminDashboard';
import ContentManager from './pages/admin/ContentManager';
import AdminRoleManager from './pages/admin/AdminRoleManager';
import UserManager from './pages/admin/UserManager';
import UnsplashTest from './pages/UnsplashTest';
import Calendar from './pages/Calendar';
import Achievements from './pages/Achievements';
import Loading from './components/shared/Loading';
import { initializeMobileOptimizations } from './utils/mobileOptimizations';

// Navigation
import MobileNav from './components/navigation/MobileNav';
import MobileHeader from './components/navigation/MobileHeader';
import Sidebar from './components/navigation/Sidebar';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Onboarding check wrapper
function OnboardingRoute({ children }) {
  const { user, loading, familyId } = useAuth();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  // Wait a moment for Firestore listener to update after sign-in
  useEffect(() => {
    if (!loading && user) {
      // Give Firestore listener time to update (especially after fresh sign-in)
      const timeout = setTimeout(() => {
        setCheckingOnboarding(false);
      }, 800);
      
      return () => clearTimeout(timeout);
    } else if (!loading && !user) {
      setCheckingOnboarding(false);
    }
  }, [loading, user]);

  if (loading || checkingOnboarding) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user doesn't have a familyId, they need to complete onboarding
  if (!familyId) {
    // Check if we're already on onboarding to avoid redirect loops
    if (window.location.pathname !== '/onboarding') {
      // Log for debugging multi-domain issues
      console.log('Redirecting to onboarding - user authenticated but no familyId', {
        userId: user.uid,
        email: user.email,
        pathname: window.location.pathname,
        hostname: window.location.hostname,
      });
      return <Navigate to="/onboarding" replace />;
    }
    return <Loading />;
  }

  return children;
}

// Admin route wrapper - requires auth, onboarding, and admin status
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin(user?.uid);

  if (loading || adminLoading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  // Initialize mobile optimizations on mount
  useEffect(() => {
    initializeMobileOptimizations();
  }, []);

  return (
    <AuthProvider>
      <FamilyProvider>
        <Router>
          <div className="App" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
            {/* Desktop Sidebar - only visible on desktop */}
            <Sidebar />
            
            {/* Mobile Navigation - only visible on mobile */}
            <MobileHeader />
            <MobileNav />
            
            {/* Main Content Area */}
            <main 
              id="main-content" 
              className="min-h-screen"
              style={{ 
                position: 'relative',
                top: 0,
                left: 0,
                transition: 'margin-left 300ms ease-in-out, width 300ms ease-in-out',
                overflowX: 'hidden',
                maxWidth: '100vw'
              }}
            >
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/accept-invite/:inviteToken"
                element={<AcceptInvite />}
              />

              {/* Onboarding - requires auth but not family setup */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes - require auth AND onboarding complete */}
              <Route
                path="/"
                element={
                  <OnboardingRoute>
                    <Dashboard />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/experiences"
                element={
                  <OnboardingRoute>
                    <Experiences />
                  </OnboardingRoute>
                }
              />
              <Route
                path="/experience/:experienceId"
                element={
                  <OnboardingRoute>
                    <ExperiencePlanning />
                  </OnboardingRoute>
                }
              />
              {/* Legacy route for backward compatibility */}
              <Route
                path="/project/:projectId"
                element={
                  <OnboardingRoute>
                    <ExperiencePlanning />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/explore"
                element={
                  <OnboardingRoute>
                    <Explore />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/explore/recipe/:recipeId"
                element={
                  <OnboardingRoute>
                    <RecipeDetail />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/explore/activity/:activityId"
                element={
                  <OnboardingRoute>
                    <ActivityDetail />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/explore/story/:storyId"
                element={
                  <OnboardingRoute>
                    <StoryDetail />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/submit-content"
                element={
                  <OnboardingRoute>
                    <SubmitContent />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/community"
                element={
                  <OnboardingRoute>
                    <Community />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <OnboardingRoute>
                    <Settings />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/chats"
                element={
                  <OnboardingRoute>
                    <Chats />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/chats/:chatId"
                element={
                  <OnboardingRoute>
                    <ChatDetail />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/calendar"
                element={
                  <OnboardingRoute>
                    <Calendar />
                  </OnboardingRoute>
                }
              />

              <Route
                path="/achievements"
                element={
                  <OnboardingRoute>
                    <Achievements />
                  </OnboardingRoute>
                }
              />

              {/* Admin routes - require admin access */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/submissions"
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/content"
                element={
                  <AdminRoute>
                    <ContentManager />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <AdminRoute>
                    <AdminRoleManager />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserManager />
                  </AdminRoute>
                }
              />
              <Route path="/unsplash-test" element={<UnsplashTest />} />

              {/* Catch all - redirect to login or dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#222',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            />
          </div>
        </Router>
      </FamilyProvider>
    </AuthProvider>
  );
}

export default App;
